// =============================================================================
// Booking Request Detail Drawer
// =============================================================================
// Drawer laterale con dettaglio di una BookingRequest + azioni approve/reject.
// =============================================================================

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { X, CheckCircle2, XCircle, Loader2, UserCheck, UserPlus, Users } from 'lucide-react';
import { useBookingRequest } from '../../api/hooks';
import { useDateLocale } from '../../hooks/useDateLocale';
import { useTenantTimeZone } from '../../store/tenantStore';
import { formatInTz, timeStringInTz } from '../../lib/tenantTime';

interface Props {
  requestId: string;
  onClose: () => void;
  onApprove: (
    id: string,
    opts?: { reviewNotes?: string; approveAs?: 'join' | 'new_appointment' },
  ) => Promise<void> | void;
  onReject: (
    id: string,
    payload: { rejectionReason?: string; reviewNotes?: string },
  ) => Promise<void> | void;
  isSubmitting: boolean;
}

export function BookingRequestDetailDrawer({
  requestId,
  onClose,
  onApprove,
  onReject,
  isSubmitting,
}: Props) {
  const { t } = useTranslation('booking-requests');
  const dateLocale = useDateLocale();
  const timeZone = useTenantTimeZone();
  const { data: request, isLoading } = useBookingRequest(requestId);

  const [mode, setMode] = useState<'view' | 'approve' | 'reject'>('view');
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  // Scelta di approvazione per le richieste di join: unisci all'evento o crea nuovo
  const [approveAs, setApproveAs] = useState<'join' | 'new_appointment'>('join');

  const snapshot = (request?.customerSnapshot ?? {}) as {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    fiscalCode?: string;
    notes?: string;
  };
  const matchedCustomer = request?.matchedCustomer ?? null;
  const allocations = (request?.proposedAllocations ?? []) as Array<{
    resourceTypeId: string;
    resourceId: string;
    startTime: string;
    endTime: string;
    role: string;
    customerPreferred?: boolean;
  }>;

  const canAct = request?.status === 'PENDING';

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={() => !isSubmitting && onClose()}
      />
      <aside className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white shadow-xl z-50 flex flex-col">
        <header className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{t('detail.title')}</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {isLoading || !request ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Servizio + slot */}
              <Section title={t('detail.service')}>
                <div className="text-base font-medium text-gray-900 flex items-center gap-2">
                  {request.serviceName}
                  {request.isJoinRequest && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                      <Users className="w-3 h-3" />
                      {t('joinRequestBadge')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {formatInTz(new Date(request.requestedStart), timeZone, 'PPPp', dateLocale)}
                  {' → '}
                  {timeStringInTz(new Date(request.requestedEnd), timeZone)}
                </div>
              </Section>

              {/* Join a evento esistente */}
              {request.isJoinRequest && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800 flex items-start gap-2">
                  <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('joinTargetInfo')}</span>
                </div>
              )}

              {/* Customer match */}
              <Section title={t('detail.customerMatch')}>
                {matchedCustomer ? (
                  <div className="flex items-start gap-2 text-sm">
                    <UserCheck className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span className="text-gray-700">
                      {t('detail.customerMatchFound', {
                        name: `${matchedCustomer.firstName} ${matchedCustomer.lastName}`,
                      })}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-sm">
                    <UserPlus className="w-4 h-4 text-amber-600 mt-0.5" />
                    <span className="text-gray-700">{t('detail.customerNotFound')}</span>
                  </div>
                )}
              </Section>

              {/* Customer data */}
              <Section title={t('detail.customerData')}>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Nome" value={`${snapshot.firstName ?? ''} ${snapshot.lastName ?? ''}`.trim()} />
                  <Field label="Email" value={snapshot.email ?? '-'} />
                  <Field label="Telefono" value={snapshot.phone ?? '-'} />
                  <Field label="Codice fiscale" value={snapshot.fiscalCode ?? '-'} />
                </dl>
                <div className="mt-3">
                  <div className="text-xs uppercase text-gray-400 mb-1">{t('detail.notes')}</div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {snapshot.notes ?? t('detail.noNotes')}
                  </div>
                </div>
              </Section>

              {/* Allocations (per i join non ci sono: le risorse sono quelle dell'evento target) */}
              {!request.isJoinRequest && (
              <Section title={t('detail.allocations')}>
                {allocations.length === 0 ? (
                  <div className="text-sm text-gray-500">—</div>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {allocations.map((a, idx) => (
                      <li key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="font-mono text-xs text-gray-600">{a.resourceId.slice(0, 8)}…</span>
                        <span className="text-xs text-gray-500">{a.role}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
              )}

              {/* Meta */}
              <Section title="">
                <dl className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                  <Field label={t('detail.submittedAt')} value={format(new Date(request.createdAt), 'PP p', { locale: dateLocale })} />
                  <Field label={t('detail.expiresAt')} value={format(new Date(request.expiresAt), 'PP', { locale: dateLocale })} />
                  <Field label={t('detail.source')} value={request.source} />
                  {request.submitterIp && <Field label={t('detail.ip')} value={request.submitterIp} />}
                </dl>
              </Section>
            </div>

            {/* Footer azioni */}
            {canAct && mode === 'view' && (
              <footer className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setMode('reject')}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200"
                >
                  {t('actions.reject')}
                </button>
                <button
                  onClick={() => setMode('approve')}
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t('actions.approve')}
                </button>
              </footer>
            )}

            {mode === 'approve' && (
              <footer className="px-6 py-4 border-t border-gray-200 space-y-3">
                <div className="text-sm font-medium text-gray-900">{t('approveDialog.title')}</div>
                <p className="text-xs text-gray-500">{t('approveDialog.description')}</p>

                {/* Solo per i join: scelta unisci-vs-nuovo appuntamento */}
                {request.isJoinRequest && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700">
                      {t('approveDialog.joinModeLabel')}
                    </div>
                    <label className="flex items-start gap-2 p-2 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="approveAs"
                        className="mt-0.5"
                        checked={approveAs === 'join'}
                        onChange={() => setApproveAs('join')}
                        disabled={isSubmitting}
                      />
                      <span>
                        <span className="block text-sm text-gray-900">{t('approveDialog.joinModeJoin')}</span>
                        <span className="block text-xs text-gray-500">{t('approveDialog.joinModeJoinHint')}</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 p-2 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="approveAs"
                        className="mt-0.5"
                        checked={approveAs === 'new_appointment'}
                        onChange={() => setApproveAs('new_appointment')}
                        disabled={isSubmitting}
                      />
                      <span>
                        <span className="block text-sm text-gray-900">{t('approveDialog.joinModeNew')}</span>
                        <span className="block text-xs text-gray-500">{t('approveDialog.joinModeNewHint')}</span>
                      </span>
                    </label>
                  </div>
                )}

                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={2}
                  placeholder={t('approveDialog.notesLabel')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setMode('view')}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100"
                  >
                    {t('approveDialog.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      onApprove(request.id, {
                        reviewNotes: reviewNotes || undefined,
                        approveAs: request.isJoinRequest ? approveAs : undefined,
                      })
                    }
                    disabled={isSubmitting}
                    className="btn-primary px-3 py-1.5 text-sm inline-flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {t('approveDialog.confirm')}
                  </button>
                </div>
              </footer>
            )}

            {mode === 'reject' && (
              <footer className="px-6 py-4 border-t border-gray-200 space-y-3">
                <div className="text-sm font-medium text-gray-900">{t('rejectDialog.title')}</div>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={2}
                  placeholder={t('rejectDialog.reasonLabel')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-red-500"
                />
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={2}
                  placeholder={t('rejectDialog.notesLabel')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-red-500"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setMode('view')}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100"
                  >
                    {t('rejectDialog.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      onReject(request.id, {
                        rejectionReason: rejectionReason || undefined,
                        reviewNotes: reviewNotes || undefined,
                      })
                    }
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <XCircle className="w-4 h-4" />
                    {t('rejectDialog.confirm')}
                  </button>
                </div>
              </footer>
            )}
          </>
        )}
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      {title && (
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-700 mt-0.5">{value}</dd>
    </div>
  );
}
